'use client';

import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import RelatedRecipeComp from 'components/RelatedRecipes';
import { GenericCardCarousel } from 'components/GenericCardCarousel';
import { useGlobalContext } from 'components/Context/hooks/useGlobalContext';
import type { IBreadcrumbTitles } from 'components/Breadcrumbs';

import type { RecipeResult, TipsAndTricksDetail, TipsAndTricks } from 'types/Recipes';
import type { TipsAndTricksCMSData } from 'types/TipsAndTricksCMSData';
import type { ISocialMediaComponent } from 'components/SocialMedia/types';
import type { HeroBannerImages } from 'types/Recipes';

import { ImagesService } from 'service/images/imagesService';
import { DictionaryService } from 'service/dictionary/dictionaryService';
import { RelatedDataService } from 'service/relatedData/relatedDataService';
import { TrackingService } from 'service/tracking/trackingService';
import { useTimerScrollTracking } from '@/hooks/useTimerScrollTracking';
import { useScrollPercentageTracking } from '@/hooks/useScrollPercentageTracking';

const Breadcrumbs = dynamic(
  () => import('components/Breadcrumbs').then((m: any) => m.Breadcrumbs),
  { ssr: true },
) as any;

const Title = dynamic(() => import('components/Typography').then((m: any) => m.Title), {
  ssr: true,
}) as any;

const Text = dynamic(() => import('components/Typography').then((m: any) => m.Text), {
  ssr: true,
}) as any;

const Grid = dynamic(() => import('components/Grid').then((m: any) => m.Grid), {
  ssr: true,
}) as any;

const GridItem = dynamic(() => import('components/Grid').then((m: any) => m.GridItem), {
  ssr: true,
}) as any;

const GridRow = dynamic(() => import('components/Grid').then((m: any) => m.GridRow), {
  ssr: true,
}) as any;

const DownArrow = dynamic(() => import('components/DownArrow').then((m: any) => m.DownArrow), {
  ssr: true,
}) as any;

const RecipeStep = dynamic(() => import('components/RecipeStep').then((m: any) => m.RecipeStep), {
  ssr: true,
}) as any;

const SustainabilityBanner = dynamic(
  () => import('components/SustainabilityBanner').then((m: any) => m.SustainabilityBanner),
  { ssr: true },
) as any;

const SocialMedia = dynamic(() => import('components/SocialMedia').then((m) => m.SocialMedia), {
  ssr: true,
});

const HeroBannerRDP = dynamic(
  () => import('components/HeroBannerRDP').then((m: any) => m.HeroBannerRDP),
  { ssr: true },
) as any;

const Spacer = dynamic(() => import('components/Spacer').then((m: any) => m.Spacer), {
  ssr: true,
});

const ClientInspirationTipsAndTricksPage = ({
  tipAndTrick,
  followUsData,
  relatedRecipes,
  tipsAndTricksData,
  relatedTipsAndTricks,
  breadcrumbsCMSData,
}: {
  tipAndTrick: TipsAndTricksDetail;
  followUsData: ISocialMediaComponent | null;
  relatedRecipes: RecipeResult[] | null;
  tipsAndTricksData: TipsAndTricksCMSData | null;
  relatedTipsAndTricks: TipsAndTricks[];
  breadcrumbsCMSData: IBreadcrumbTitles;
}) => {
  useTimerScrollTracking({
    timeOut: 30 * 1000, // 30 seconds
    scrollPercentage: 25,
    eventCb: TrackingService.recipePageTimeScrollPercentage,
  });
  useScrollPercentageTracking({
    percentageValues: [25, 50, 75, 100],
    eventCb: TrackingService.recipePageScroll,
  });

  const { pathTranslations } = useGlobalContext();
  const dictionary = tipsAndTricksData?.titles?.dictionaryItems;
  const relatedRecipesData = {
    seeMore: DictionaryService.getCopy('see_recipe_details', dictionary) || '',
    relatedRecipesSubTitle: DictionaryService.getCopy('related_recipe', dictionary) || '',
    relatedRecipesTitle: DictionaryService.getCopy('related_recipetitle', dictionary) || '',
    difficulty: {
      0: DictionaryService.getCopy('easy', dictionary),
      1: DictionaryService.getCopy('intermediate', dictionary),
      2: DictionaryService.getCopy('tough', dictionary),
    },
  };
  const breadcrumbsCategories = [
    {
      slug: pathTranslations.tipsAndTricks,
      displayName: breadcrumbsCMSData?.tipsAndTricksText?.value,
    },
  ];
  const tipAndTrickData = RelatedDataService.getRelatedData(
    [tipAndTrick],
    pathTranslations,
    'tipsAndTricks',
  )[0];
  const parsedTipsAndTricksCardData = RelatedDataService.getRelatedData(
    relatedTipsAndTricks?.filter((tip) => tip.Id !== tipAndTrick.Id),
    pathTranslations,
    'tipsAndTricks',
  );

  useEffect(() => {
    if (tipAndTrick) {
      TrackingService.contentGroups({
        cg1: 'tips&tricks',
        cg5: tipAndTrick?.Category?.GlobalCategory?.Title || '',
      });
    }
  }, []);

  const tipAndTrickImages: HeroBannerImages =
    ImagesService.getRecipeImageForHeroBanner(tipAndTrick);

  return (
    <>
      <Breadcrumbs
        titles={breadcrumbsCMSData}
        pageName={tipAndTrickData?.title}
        categories={breadcrumbsCategories}
      />
      <div>
        {tipAndTrickImages.desktop && tipAndTrickImages.mobile ? (
          <>
            <HeroBannerRDP image={tipAndTrickImages} mobileImage={tipAndTrickImages} />
            <DownArrow />
          </>
        ) : (
          <Spacer />
        )}
        <Grid>
          <GridRow className="mt-20 mb-40">
            <Title tag="h1" type="lg" className="text-center">
              {tipAndTrickData?.title}
            </Title>
            <Text tag="div" type="md" className="text-center">
              {tipAndTrickData?.description}
            </Text>
          </GridRow>
        </Grid>
        <Grid>
          <GridRow columns={16}>
            <GridItem colSpan={8} colStart={5}>
              {tipAndTrick?.ClassicInstruction?.PreparationBlocks && (
                <RecipeStep
                  steps={tipAndTrick.ClassicInstruction.PreparationBlocks}
                  showAssets
                  dictionary={tipsAndTricksData?.titles?.dictionaryItems ?? []}
                  isFaqStructure={tipAndTrick.HasFaqStructure}
                  recipeTitle=""
                  settingRateData={{
                    isRatingGiven: false,
                    ratingGiven: '',
                    setIsRatingGiven: () => {},
                    setRatingGiven: () => {},
                    comment: '',
                    setComment: () => {},
                  }}
                  ratingMsg=""
                  ratingData=""
                />
              )}
            </GridItem>
          </GridRow>
        </Grid>
        {tipsAndTricksData?.sustainabilityBanner && (
          <SustainabilityBanner {...tipsAndTricksData?.sustainabilityBanner} />
        )}
        {parsedTipsAndTricksCardData?.length > 0 && tipsAndTricksData && (
          <GenericCardCarousel
            {...tipsAndTricksData?.relatedTipsCarouselData}
            algoliaItems={parsedTipsAndTricksCardData}
            cardVariation={'tipsAndTricks'}
            dictionaryItem={
              tipsAndTricksData?.titles?.dictionaryItems?.filter(
                (item: { key: string }) => item.key === 'read_more',
              ) || [{ value: 'Read more', key: 'read_more' }]
            }
          />
        )}
        {relatedRecipes && relatedRecipes.length > 0 && (
          <RelatedRecipeComp
            currentRecipe={tipAndTrick}
            linkName={relatedRecipesData.seeMore}
            relatedRecipe={relatedRecipes}
            copy={relatedRecipesData.difficulty}
            subTitle={relatedRecipesData.relatedRecipesSubTitle}
            title={relatedRecipesData.relatedRecipesTitle}
          />
        )}
        {!!followUsData && (
          <SocialMedia
            id={followUsData.id}
            super={followUsData.super}
            title={followUsData.title}
            socialMedia={followUsData.socialMedia}
            middlegroundImage={followUsData.middlegroundImage}
            foregroundImage={followUsData.foregroundImage}
          />
        )}
      </div>
    </>
  );
};

export default ClientInspirationTipsAndTricksPage;
